import React, { useState, useEffect, useRef } from "react";
import queueApi from "../services/api";
import signalRService from "../services/signalR";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageToggle from "./LanguageToggle";
import photo_logo from "../images/icon.svg";
import "./QueueWarehouse.css";
import imageGroup from "../images/groups.svg";
import imageNotification from "../images/ion_notifcations.svg";
import "./Skeleton.css";
import { parseDateInTajikistan, formatTajikistanTime, formatTajikistanDate } from "../utils/time";

const WarehouseSkeleton = () => (
    <div className="ds-monitor-root" style={{overflow: 'hidden'}}>
        <header className="ds-header">
            <div className="skeleton-base" style={{width: '350px', height: '50px'}}></div>
            <div className="skeleton-base" style={{width: '200px', height: '50px'}}></div>
        </header>
        <div className="ds-content">
            <div className="ds-column">
                <div className="skeleton-base" style={{width: '200px', height: '35px', marginBottom: '20px'}}></div>
                {[1, 2, 3, 4,5,6,7,8,9].map(i => <div key={i} className="skeleton-base skeleton-card-monitor"></div>)}
            </div>
            <div className="ds-main-divider"></div>
            <div className="ds-column">
                <div className="skeleton-base" style={{width: '200px', height: '35px', marginBottom: '20px'}}></div>
                {[1, 2, 3, 4,5,6,7,8].map(i => <div key={i} className="skeleton-base skeleton-card-monitor"></div>)}
            </div>
        </div>
    </div>
);
const QueueWarehouse = () => {
    const mountedRef = useRef(true);
    const { getText } = useLanguage();

    const [currentTime, setCurrentTime] = useState(new Date());
    const [queueData, setQueueData] = useState({ called: [], waiting: [], countWaiting: 0, countCalled: 0 });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [clickCount, setClickCount] = useState(0);
    const [token, setToken] = useState('');
    const [modalError, setModalError] = useState('');
    const [modalLoading, setModalLoading] = useState(false);

    const calculateMinutesElapsed = (dateCreated) => {
        if (!dateCreated) return "0";
        const start = parseDateInTajikistan(dateCreated);
        const diffMs = Math.max(0, new Date() - start);
        return Math.floor(diffMs / 60000);
    };

    useEffect(() => {
        if (clickCount >= 3) {
            setShowModal(true);
            setClickCount(0);
        }
    }, [clickCount]);

    const handleLogoClick = () => {
        setClickCount(prev => prev + 1);
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        setModalLoading(true);
        setModalError('');
        try {
            queueApi.setToken(token);
            const response = await queueApi.getServices();
            if (response) {
                setShowModal(false);
                window.location.reload();
            } else {
                setModalError('Токен нодуруст аст ё сервер дастрас нест. Консолро тафтиш кунед.');
            }
        } catch (err) {
            console.error('Token validation error:', err);
            setModalError('Хатои пайвастшавӣ ба сервер. Шабака ва URL-ро санҷед.');
        } finally {
            setModalLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialQueue = async () => {
            try {
                const terminalInfo = await queueApi.getTerminalInfo();
                if (terminalInfo?.id) {
                    await signalRService.connect(terminalInfo.id);
                }

                // Использование refreshQueue для загрузки и кеширования всех билетов
                await signalRService.refreshQueue();
                
                // Получение обновленного состояния с кешированными данными
                const currentView = signalRService.currentQueueState;

                if (mountedRef.current && currentView) {
                    // Filter to show only warehouse queues (phone services)
                    const filteredCalled = (currentView.called || []).filter(item => 
                        item.isPhoneNumber === true || item.terminalService?.service?.isFromPhoneNumber === true
                    );
                    const filteredWaiting = (currentView.waiting || []).filter(item => 
                        item.isPhoneNumber === true || item.terminalService?.service?.isFromPhoneNumber === true
                    );

                    setQueueData({
                        called: Array.isArray(filteredCalled) ? filteredCalled : [],
                        waiting: Array.isArray(filteredWaiting) ? filteredWaiting : [],
                        countWaiting: filteredWaiting.length,
                        countCalled: filteredCalled.length
                    });
                } else {
                    console.log("❌ [Warehouse] No currentView or component unmounted");
                }
            } catch (err) {
                console.error("❌ [Warehouse] Хатои боркунӣ:", err);
            } finally {
                if (mountedRef.current) setLoading(false);
            }
        };

        const unsubscribe = signalRService.onQueueView((view) => {
            if (mountedRef.current && view) {
                // Filter to show only warehouse queues (phone services)
                const filteredCalled = (view.called || []).filter(item => 
                    item.isPhoneNumber === true || item.terminalService?.service?.isFromPhoneNumber === true
                );
                const filteredWaiting = (view.waiting || []).filter(item => 
                    item.isPhoneNumber === true || item.terminalService?.service?.isFromPhoneNumber === true
                );

                setQueueData({
                    called: Array.isArray(filteredCalled) ? filteredCalled : [],
                    waiting: Array.isArray(filteredWaiting) ? filteredWaiting : [],
                    countWaiting: filteredWaiting.length,
                    countCalled: filteredCalled.length
                });
            }
        });

        loadInitialQueue();

        return () => {
            mountedRef.current = false;
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        let wasOffline = false;

        const handleOffline = () => {
            wasOffline = true;
        };

        const handleOnline = () => {
            if (wasOffline) {
                wasOffline = false;
                window.location.reload();
            }
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    const getClientLabels = () => ({
        phone: getText('ТЕЛЕФОН', 'ТЕЛЕФОН'),
        shelf: getText('ПОЛКА', 'ПОЛКА'),
        waiting: getText('ЖДЕТ', 'ИНТИЗОР'),
        place: getText('МЕСТО', 'ҶОЙ'),
        window: getText('Окно', 'Мизи')
    });

    const clientLabels = getClientLabels();

    const formatShelfCodes = (codes) => {
        if (!codes || codes.trim() === "") return "---";
        const items = codes.split(',').map(s => s.trim()).filter(s => s);
        if (items.length === 0) return "---";
        if (items.length <= 3) return items.join(', ');
        return items.slice(0, 3).join(', ') + '...';
    };

    if (loading) return <WarehouseSkeleton />;

    return (
        <div className="ds-monitor-root">

            <header className="ds-header">
                <div className="ds-header-left" onClick={handleLogoClick}>
                    <img src={photo_logo} className="ds-logo" alt="logo" />
                    <h1 className="ds-title">{getText('ПАНЕЛЬ ', 'ЛАВҲАИ ')} <span className="ds-title-bold">{getText('МОНИТОРИНГА', 'МОНИТОРИНГӢ')}</span></h1>
                </div>
                <div className="ds-header-right">
                    <div className="ds-clock">{formatTajikistanTime(currentTime)}</div>
                    <div className="ds-date">{formatTajikistanDate(currentTime)}</div>
                </div>
            </header>

            <main className="ds-content">
                {/* СУТУНИ ЧАП: ЛИСТ ОЖИДАНИЯ */}
                <section className="ds-column">
                    <div className="ds-column-header">
                        <h2 className="ds-column-title">{getText('ЛИСТ ОЖИДАНИЯ', 'РӮЙХАТИ ИНТИЗОРӢ')}</h2>
                        <div className="ds-badge">{queueData.waiting.length} {getText('клиентов', 'мизоҷ')}</div>
                    </div>
                    <div className="ds-list">
                        {queueData.waiting.length > 0 ? queueData.waiting.map((item, index) => {
                            const stableCache = signalRService.getStableCache();
                            const cachedTicket = stableCache.tickets?.[item.id];
                            return (
                                <div key={index} className="ds-card ds-card-waiting">
                                    <div className="ds-section ds-section-phone">
                                        <span className="ds-label-top">{clientLabels.phone}</span>
                                        <span className="ds-value-main">{item.clientPhoneNumber?.startsWith('992') ? item.clientPhoneNumber.slice(3) : (item.clientPhoneNumber || cachedTicket?.clientPhoneNumber || "---")} (FC{String(item.clientId).padStart(7, '0')})</span>
                                    </div>
                                    <div className="ds-divider-v"></div>
                                    <div className="ds-section ds-section-shelf">
                                        <span className="ds-label-top">{clientLabels.shelf}</span>
                                        <span className="ds-value-sub">{formatShelfCodes(item.shelfCodes || item.shelfId)}</span>
                                    </div>
                                    <div className="ds-section ds-section-wait">
                                        <span className="ds-label-top">{clientLabels.waiting}</span>
                                        <span className="ds-value-small">{calculateMinutesElapsed(item.dateCreated)} {getText('мин', 'дақ')}</span>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="empty-state">
                                <img src={imageGroup} className="empty-img-groups" alt="empty" />
                                <p className="empty-text">{getText('В очереди никого нет', 'Дар навбат ҳеҷ кас нест')}</p>
                            </div>
                        )}
                    </div>
                </section>

                <div className="ds-main-divider"></div>

                {/* СУТУНИ РОСТ: В ОБСЛУЖИВАНИИ */}
                <section className="ds-column">
                    <div className="ds-column-header">
                        <h2 className="ds-column-title">{getText('В ОБСЛУЖИВАНИИ', 'ДАР ХИЗМАТ')}</h2>
                        <div className="ds-badge ds-badge-active">{queueData.called.length} {getText('активно', 'фаъол')}</div>
                    </div>
                    <div className="ds-list">
                        {queueData.called.length > 0 ? queueData.called.map((item, index) => {
                            // Get cached data for testing
                            const stableCache = signalRService.getStableCache();
                            const cachedTicket = stableCache.tickets?.[item.id];
                            
                            return (
                                <div key={index} className="ds-card ds-card-active">
                                    <div className="ds-section ds-section-phone">
                                        <span className="ds-label-top">{clientLabels.phone}</span>
                                        <span className="ds-value-main">{item.clientPhoneNumber?.startsWith('992') ? item.clientPhoneNumber.slice(3) : (item.clientPhoneNumber || cachedTicket?.clientPhoneNumber || "---")} (FC{String(item.clientId).padStart(7, '0')})</span>
                                    </div>
                                    <div className="ds-divider-v"></div>
                                    <div className="ds-section ds-section-shelf">
                                        <span className="ds-label-top">{clientLabels.shelf}</span>
                                        <span className="ds-value-sub">{formatShelfCodes(item.shelfId || cachedTicket?.shelfCodes)}</span>
                                    </div>
                                    <div className="ds-section ds-section-wait">
                                        <span className="ds-label-top">{clientLabels.place}</span>
                                        <span className="ds-value-small">
                                            {`хазинаи ${item.windowNumber}`}
                                        </span>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="empty-state">
                                <img src={imageNotification} className="empty-img-notif" alt="empty" />
                                <p className="empty-text">{getText('Ожидайте приглашения', 'Интизори даъват бошед')}</p>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{getText('API Key Configuration', 'Танзимоти калиди API')}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleModalSubmit}>
                            <div className="modal-body">
                                <div className="input-group">
                                    <label>{getText('Введите ваш API ключ:', 'Калиди API-и худро ворид кунед:')}</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="API Key"
                                        value={token}
                                        onChange={e => setToken(e.target.value)}
                                        required
                                    />
                                </div>
                                {modalError && <div className="error-message">{modalError}</div>}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="button button-cancel" onClick={() => setShowModal(false)}>{getText('Отмена', 'Бекор кардан')}</button>
                                <button type="submit" className="button button-submit" disabled={modalLoading}>
                                    {modalLoading ? getText('Сохранение...', 'Нигоҳ доштан...') : getText('Сохранить', 'Нигоҳ доред')}
                                </button>
                            </div>
                        </form>
                        
                        {/*<LanguageToggle />*/}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueueWarehouse;